import type {
    AnalyticsParams,
    NativeEvent,
    NativeEventHandler,
    NativeEventMap,
    NativeOutboundMessage,
    NativeOutboundMessageMap,
    Platform,
} from './types';

function detectPlatform(): Platform {
    if (window.AndroidBridge) return 'android';
    if (window.webkit?.messageHandlers?.nativeBridge) return 'ios';

    return 'unknown';
}

/**
 * Subscribers per native event. Stored with an erased handler type — the
 * public `on`/`off`/`emit` signatures keep callers fully typed, and the
 * payload is cast back at each boundary.
 */
type ErasedHandler = (payload: never) => void;
const listeners = new Map<NativeEvent, Set<ErasedHandler>>();

function emit<E extends NativeEvent>(event: E, payload: NativeEventMap[E]): void {
    const handlers = listeners.get(event);

    if (!handlers || handlers.size === 0) {
        console.warn(`[Bridge] Native event "${event}" received but no JS handler is subscribed.`);

        return;
    }

    // Copy so a handler that unsubscribes mid-dispatch doesn't disturb iteration.
    [...handlers].forEach((handler) => (handler as NativeEventHandler<E>)(payload));
}

/**
 * Install the global functions the native shell calls (native -> JS).
 * Native invokes e.g. `window.navigateNext()`; we fan that out to subscribers.
 * Idempotent — safe if the module is evaluated more than once.
 */
function installNativeReceivers(): void {
    window.navigateNext = () => emit('navigateNext', undefined);
    window.navigatePrev = () => emit('navigatePrev', undefined);
}

installNativeReceivers();

/**
 * Unified two-way bridge between the WebView and the native shell.
 * Components import `{ bridge }` and never touch platform-specific APIs directly.
 */
export const bridge = {
    platform: detectPlatform(),

    /**
     * Subscribe to an event sent by the native shell (native -> JS).
     * Returns an unsubscribe function.
     */
    on<E extends NativeEvent>(event: E, handler: NativeEventHandler<E>): () => void {
        const handlers = listeners.get(event) ?? new Set<ErasedHandler>();

        listeners.set(event, handlers);

        const erased = handler as ErasedHandler;

        handlers.add(erased);

        return () => handlers.delete(erased);
    },

    /** Remove a previously registered handler. */
    off<E extends NativeEvent>(event: E, handler: NativeEventHandler<E>): void {
        listeners.get(event)?.delete(handler as ErasedHandler);
    },

    /**
     * Send an analytics event to the native shell (JS -> native).
     * Mirrors the Android `AndroidBridge.sendAnalyticsEvent(name, json)` interface;
     * on iOS it routes through the `nativeBridge` message handler.
     */
    sendAnalyticsEvent(eventName: string, params: AnalyticsParams = {}): void {
        if (window.AndroidBridge) {
            // Android interfaces only accept strings.
            window.AndroidBridge.sendAnalyticsEvent(eventName, JSON.stringify(params));
        } else if (window.webkit?.messageHandlers?.nativeBridge) {
            window.webkit.messageHandlers.nativeBridge.postMessage({
                method: 'sendAnalyticsEvent',
                eventName,
                params,
            });
        } else {
            console.warn('[Bridge] No native bridge available. Analytics event dropped:', eventName, params);
        }
    },

    /**
     * Send a structured, non-analytics message to native (JS -> native).
     * Mirrors the Android `AndroidBridge.postMessage(json)` interface;
     * on iOS it routes through the `nativeBridge` message handler.
     */
    send<M extends NativeOutboundMessage>(method: M, payload: NativeOutboundMessageMap[M]): void {
        if (window.AndroidBridge) {
            // Android interfaces only accept strings.
            window.AndroidBridge.postMessage(JSON.stringify({ method, payload }));
        } else if (window.webkit?.messageHandlers?.nativeBridge) {
            window.webkit.messageHandlers.nativeBridge.postMessage({ method, payload });
        } else {
            console.warn('[Bridge] No native bridge available. Message dropped:', method, payload);
        }
    },
};

export type {
    AnalyticsParams,
    NativeEvent,
    NativeEventHandler,
    NativeEventMap,
    NativeOutboundMessage,
    NativeOutboundMessageMap,
    Platform,
} from './types';
